import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Star, BadgeCheck, Clock, Flame } from 'lucide-react';
interface Testimonial {
  body: string;
  author: string;
  location: string;
  result: string;
  avatar: string;
  date: string;
}
const testimonials: Testimonial[] = [{
  body: 'Compré esto el viernes para el cumpleaños de mi papá y quedó increíble. Lo imprimí, lo metí en una carpeta y parecía un libro de $30.000. Mi viejo lo leyó todo el sábado y el asado del domingo fue el mejor que ha hecho en años. La técnica del reposo es un cambio de vida.',
  author: 'Francisca M.',
  location: 'Santiago',
  result: 'Papá pasó de "carne zapato" a aplauso familiar en 1 semana',
  avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
  date: '11 de junio, 2025'
}, {
  body: "Andaba con $7.000 en el bolsillo y sin idea qué regalarle al viejo. Vi esto y dije 'lo intento'. Oye, es que vale cada peso. El manual explica los cortes chilenos como ningún libro argentino que había visto antes. Mi papá ya habla de Abastero y Punta de Ganso como un profesional.",
  author: 'Rodrigo C.',
  location: 'Concepción',
  result: 'Regalo de $50.000 percibido por $6.390 reales',
  avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
  date: '9 de junio, 2025'
}, {
  body: 'Honestamente dudé porque era digital y barato. Pensé que iba a ser un PDF de 5 páginas con fotos de Google. Para nada. Son 60 páginas reales con fotos increíbles y técnicas que mi papá (que lleva 30 años asando) no conocía. Quedó impresionado y eso no es fácil.',
  author: 'Valentina R.',
  location: 'Viña del Mar',
  result: 'Convenció hasta al parrillero más escéptico de la familia',
  avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
  date: '5 de junio, 2025'
}];
// Custom Chilean flag icon (no emoji).
function ChileFlag({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 30 20" className={className} role="img" aria-label="Chile" data-id="element-219">
      <rect width="30" height="20" fill="#fff" data-id="element-220" />
      <rect width="30" height="10" y="10" fill="#d52b1e" data-id="element-221" />
      <rect width="10" height="10" fill="#0039a6" data-id="element-222" />
      <polygon points="5,2 5.9,4.6 8.6,4.6 6.4,6.2 7.2,8.8 5,7.2 2.8,8.8 3.6,6.2 1.4,4.6 4.1,4.6" fill="#fff" data-id="element-223" />
    </svg>;
}
// Trustpilot-style 5-star rating: green squares with a white star.
function TrustpilotStars() {
  return <div className="flex items-center gap-1" aria-label="5 de 5 estrellas" data-id="element-224">
      {[...Array(5)].map((_, i) => <span key={i} className="bg-[#00b67a] w-6 h-6 flex items-center justify-center rounded-[3px]" data-id="element-225">
          <Star size={15} className="text-white" fill="currentColor" strokeWidth={0} data-id="element-226" />
        </span>)}
    </div>;
}
export function TestimonialsSection() {
  return <section className="bg-bbq-surface py-24 px-4 border-y border-white/5" data-id="element-227">
      <div className="max-w-6xl mx-auto" data-id="element-228">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-229">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-white uppercase tracking-wide mb-6 flex items-center justify-center gap-3 flex-wrap" data-id="element-230">
            Lo que dicen los que ya lo tienen
            <ChileFlag className="w-9 h-6 rounded-sm shadow-md inline-block align-middle" data-id="element-231" />
          </h2>
          <div className="flex items-center justify-center gap-3" data-id="element-232">
            <TrustpilotStars data-id="element-233" />
            <span className="text-gray-300 text-sm font-medium" data-id="element-234">
              <span className="text-white font-semibold" data-id="element-235">Excelente</span> ·
              basado en cientos de compras
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-236">
          {testimonials.map((t, index) => <motion.figure key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.15
        }} className="bg-bbq-dark p-7 rounded-xl border border-white/10 flex flex-col" data-id="element-237">
              {/* Header: avatar + name */}
              <div className="flex items-center gap-3 mb-4" data-id="element-238">
                <img src={t.avatar} alt={`Foto de ${t.author}`} className="w-12 h-12 rounded-full object-cover border-2 border-bbq-gold/30 shrink-0" loading="lazy" data-id="element-239" />
                <div className="min-w-0" data-id="element-240">
                  <div className="flex items-center gap-1.5" data-id="element-241">
                    <span className="text-white font-semibold truncate" data-id="element-242">
                      {t.author}
                    </span>
                    <BadgeCheck size={15} className="text-[#00b67a] shrink-0" aria-label="Compra verificada" data-id="element-243" />
                  </div>
                  <p className="text-gray-500 text-sm truncate" data-id="element-244">{t.location}</p>
                </div>
              </div>

              {/* Stars */}
              <div className="mb-4" data-id="element-245">
                <TrustpilotStars data-id="element-246" />
              </div>

              {/* Body */}
              <blockquote className="text-gray-300 mb-6 text-sm md:text-base leading-relaxed flex-1" data-id="element-247">
                {t.body}
              </blockquote>

              {/* Footer */}
              <figcaption className="border-t border-white/10 pt-4 mt-auto" data-id="element-248">
                <div className="inline-block bg-bbq-surface px-3 py-1.5 rounded text-xs text-bbq-gold font-medium border border-bbq-gold/20 mb-2" data-id="element-249">
                  Resultado: {t.result}
                </div>
                <p className="text-gray-600 text-xs" data-id="element-250">{t.date}</p>
              </figcaption>
            </motion.figure>)}
        </div>

        {/* Urgency / social proof */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 max-w-3xl mx-auto bg-bbq-dark border border-bbq-ember/30 rounded-2xl p-8 md:p-10 text-center shadow-[0_0_40px_rgba(232,98,45,0.08)]" data-id="element-251">
          <p className="text-xl md:text-2xl font-semibold text-white inline-flex items-start justify-center gap-3 leading-snug" data-id="element-252">
            <Clock size={26} className="text-bbq-ember shrink-0 mt-0.5" aria-hidden="true" data-id="element-253" />
            <span data-id="element-254">
              Más de{' '}
              <span className="text-bbq-gold font-bold" data-id="element-255">
                500 papás chilenos
              </span>{' '}
              ya tienen este manual.
            </span>
          </p>
          <p className="mt-4 text-lg md:text-xl text-gray-300 inline-flex items-start justify-center gap-3 leading-snug" data-id="element-256">
            <Flame size={24} className="text-bbq-ember shrink-0 mt-0.5" aria-hidden="true" data-id="element-257" />
            <span data-id="element-258">¿El tuyo va a ser el único que siga quemando la carne?</span>
          </p>
        </motion.div>
      </div>
    </section>;
}